
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { SportType } from '@/types/sport';

export const useClubOperations = () => {
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const createClub = async (formData: {
    clubName: string;
    clubWebsite: string;
    clubLocation: string;
    clubProfilePicture: string;
    clubSport: SportType | '';
  }) => {
    // Get the default organization for new clubs
    const { data: orgData, error: orgError } = await supabase
      .from('organizations')
      .select('id')
      .eq('name', 'Default Organization')
      .single();

    if (orgError) throw orgError;

    const { error } = await supabase
      .from('clubs')
      .insert({ 
        name: formData.clubName,
        website: formData.clubWebsite || null,
        location: formData.clubLocation || null,
        profile_picture_url: formData.clubProfilePicture || null,
        sport: (formData.clubSport as SportType) || null,
        organization_id: orgData.id
      });

    if (error) throw error;
  };

  const updateClub = async (clubId: string, formData: {
    clubName: string;
    clubWebsite: string;
    clubLocation: string;
    clubProfilePicture: string;
    clubSport: SportType | '';
  }) => {
    const { error } = await supabase
      .from('clubs')
      .update({ 
        name: formData.clubName,
        website: formData.clubWebsite || null,
        location: formData.clubLocation || null,
        profile_picture_url: formData.clubProfilePicture || null,
        sport: (formData.clubSport as SportType) || null
      })
      .eq('id', clubId);

    if (error) throw error;
  };

  const deleteClub = async (clubId: string) => {
    // First check if there are any teams associated with this club
    const { data: teams, error: teamsError } = await supabase
      .from('teams')
      .select('id, name')
      .eq('club_id', clubId);

    if (teamsError) {
      console.error('Error checking teams:', teamsError);
      throw new Error(`Failed to check teams: ${teamsError.message}`);
    }


    if (teams && teams.length > 0) {
      const teamNames = teams.map(t => t.name).join(', ');
      throw new Error(`Cannot delete club with existing teams. Please delete the following teams first: ${teamNames}`);
    }

    // Check for seasons
    const { data: seasons, error: seasonsError } = await supabase
      .from('seasons')
      .select('id, name')
      .eq('club_id', clubId);

    if (seasonsError) {
      console.error('Error checking seasons:', seasonsError);
      throw new Error(`Failed to check seasons: ${seasonsError.message}`);
    }


    if (seasons && seasons.length > 0) {
      const seasonNames = seasons.map(s => s.name).join(', ');
      throw new Error(`Cannot delete club with existing seasons. Please delete the following seasons first: ${seasonNames}`);
    }

    // Delete the club
    const { error: deleteError } = await supabase
      .from('clubs')
      .delete()
      .eq('id', clubId);

    if (deleteError) {
      console.error('Error deleting club:', deleteError);
      throw new Error(`Failed to delete club: ${deleteError.message}`);
    }

  };

  const invalidateClubQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['clubs'] });
    queryClient.invalidateQueries({ queryKey: ['organization-clubs'] });
  };

  return {
    isSubmitting,
    isDeleting,
    setIsSubmitting,
    setIsDeleting,
    createClub,
    updateClub,
    deleteClub,
    invalidateClubQueries
  };
};
